import type { NavSectionProps } from "src/components/nav-section";
import type {
  Theme,
  SxProps,
  CSSObject,
  Breakpoint,
} from "@mui/material/styles";

import { varAlpha, mergeClasses } from "minimal-shared/utils";

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

import { Logo } from "src/components/logo";
import { Scrollbar } from "src/components/scrollbar";
import { NavSectionMini, NavSectionVertical } from "src/components/nav-section";

import { layoutClasses } from "../core/classes";
// import { NavUpgrade } from '../components/nav-upgrade';
import { NavToggleButton } from "../components/nav-toggle-button";

// ----------------------------------------------------------------------

export type NavVerticalProps = React.ComponentProps<"div"> & {
  isNavMini: boolean;
  sx?: SxProps<Theme>;
  cssVars?: CSSObject;
  layoutQuery?: Breakpoint;
  onToggleNav: () => void;
  data: NavSectionProps["data"];
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
};

export function NavVertical({
  sx,
  data,
  slots,
  cssVars,
  className,
  isNavMini,
  onToggleNav,
  layoutQuery = "md",
  ...other
}: NavVerticalProps) {
  const renderNavVertical = () => (
    <>
      {slots?.topArea ?? (
        <Box sx={{ pl: 3.5, pt: 2.5, pb: 1 }}>
          <Logo />
        </Box>
      )}

      <Scrollbar fillContent>
        <NavSectionVertical
          data={data}
          cssVars={cssVars}
          sx={{ px: 2, flex: "1 1 auto" }}
        />

        {/* {slots?.bottomArea ?? <NavUpgrade />} */}
      </Scrollbar>
    </>
  );

  const renderNavMini = () => (
    <>
      {slots?.topArea ?? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2.5 }}>
          <Logo isSingle={true} />
        </Box>
      )}

      <NavSectionMini
        data={data}
        cssVars={cssVars}
        sx={[
          (theme) => ({
            ...theme.mixins.hideScrollY,
            pb: 2,
            px: 0.5,
            flex: "1 1 auto",
            overflowY: "auto",
          }),
        ]}
      />

      {slots?.bottomArea}
    </>
  );

  return (
    <NavRoot
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      className={mergeClasses([
        layoutClasses.nav.root,
        layoutClasses.nav.vertical,
        className,
      ])}
      sx={sx}
      {...other}
    >
      <NavToggleButton
        isNavMini={isNavMini}
        onClick={onToggleNav}
        sx={[
          (theme) => ({
            display: "none",
            [theme.breakpoints.up(layoutQuery)]: { display: "inline-flex" },
          }),
        ]}
      />
      {isNavMini ? renderNavMini() : renderNavVertical()}
    </NavRoot>
  );
}

// ----------------------------------------------------------------------

const NavRoot = styled("div", {
  shouldForwardProp: (prop: string) =>
    !["isNavMini", "layoutQuery", "sx"].includes(prop),
})<Pick<NavVerticalProps, "isNavMini" | "layoutQuery">>(
  ({ isNavMini, layoutQuery = "md", theme }) => ({
    top: 0,
    left: 0,
    height: "100%",
    display: "none",
    position: "fixed",
    flexDirection: "column",
    zIndex: "var(--layout-nav-zIndex)",
    backgroundColor: "var(--layout-nav-bg)",
    width: isNavMini
      ? "var(--layout-nav-mini-width)"
      : "var(--layout-nav-vertical-width)",
    borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey["500Channel"], 0.12)})`,
    transition: theme.transitions.create(["width"], {
      easing: "var(--layout-transition-easing)",
      duration: "var(--layout-transition-duration)",
    }),
    [theme.breakpoints.up(layoutQuery)]: { display: "flex" },
  })
);
